import { useEffect, useState } from 'react';
import type { AgilePlanApproval, AgilePlanRowResult } from './types';
import {
  AGILE_PLAN_DRY_RUN_LINE,
  AGILE_PLAN_GROUP_LABEL,
  AGILE_PLAN_LIST_LABEL,
  agilePlanChangeLine,
  agilePlanExpiry,
  agilePlanResultFor,
  agilePlanResultLine,
  agilePlanRowHeading,
  agilePlanRowLabel,
  agilePlanTeamworkLine,
} from './agilePlanApprovalText';

interface AgilePlanApprovalCardProps {
  card: AgilePlanApproval;
  busy?: boolean;
  onApprove: () => void;
  onDecline: () => void;
  /** After Approve: what happened to each row. With results the card has no buttons. */
  results?: AgilePlanRowResult[];
  /** Emjae's first-person summary of the results, shown as it came. */
  resultText?: string;
  /** For the expiry line; defaults to the current time (and keeps ticking). */
  now?: Date;
}

function useNow(given?: Date): Date {
  const [ticking, setTicking] = useState(() => new Date());
  useEffect(() => {
    if (given) return undefined;
    const timer = setInterval(() => setTicking(new Date()), 30_000);
    return () => clearInterval(timer);
  }, [given]);
  return given ?? ticking;
}

/**
 * The card Emjae shows before it changes a client's Agile Plan: ONE card for the whole request, a row per
 * change, edits field by field (old to new), notes with the removed and added text marked, invalid rows with
 * their errors (never applied), who a Teamwork sync will run as, and a 30 minute expiry. Approve applies the
 * rows Core stored with the card; Cancel applies nothing. Everything is plain text.
 */
export function AgilePlanApprovalCard({ card, busy = false, onApprove, onDecline, results, resultText, now }: AgilePlanApprovalCardProps) {
  const current = useNow(now);
  const [clicked, setClicked] = useState<'approve' | 'cancel' | null>(null);
  const expiry = agilePlanExpiry(card, current);
  const settled = results !== undefined;
  const disabled = busy || clicked !== null;
  const click = (which: 'approve' | 'cancel', callback: () => void) => {
    if (disabled) return;
    setClicked(which);
    callback();
  };
  return (
    <div role="group" aria-label={AGILE_PLAN_GROUP_LABEL} className="mt-2 rounded-lg border border-warm-border bg-paper px-3 py-2.5">
      {card.dryRun && (
        <p role="status" className="mb-2 rounded-md border border-warm-border bg-paper-2 px-2 py-1 font-mono text-[11px] text-ink-2">
          {AGILE_PLAN_DRY_RUN_LINE}
        </p>
      )}
      <ul role="list" aria-label={AGILE_PLAN_LIST_LABEL} className="grid gap-2">
        {card.rows.map((row, position) => {
          const teamwork = agilePlanTeamworkLine(row);
          const result = agilePlanResultFor(results, row.index);
          return (
            <li key={row.index} aria-label={agilePlanRowLabel(position, card.rows.length)} className="rounded-md border border-warm-border bg-paper-2 px-2.5 py-2">
              <p className="font-display text-[14px] leading-tight text-ink">{agilePlanRowHeading(row)}</p>
              {row.valid && row.changes.map((change) => (
                <p key={change.field} className="mt-1 text-[12px] text-ink-2">{agilePlanChangeLine(change)}</p>
              ))}
              {row.valid && row.diff && (
                <p className="mt-1 whitespace-pre-wrap text-[12px] text-ink-2">
                  {row.diff.removed ? <del className="text-destructive">{row.diff.removed}</del> : null}
                  {row.diff.removed && row.diff.added ? ' ' : null}
                  {row.diff.added ? <ins className="text-ink no-underline">{row.diff.added}</ins> : null}
                </p>
              )}
              {teamwork && <p className="mt-1 font-mono text-[11px] text-ink-3">{teamwork}</p>}
              {row.valid && row.noop && <p className="mt-1 text-[12px] text-ink-3">No change</p>}
              {!row.valid && (
                <>
                  {row.errors.map((error) => (
                    <p key={error} className="mt-1 text-[12px] text-destructive">{error}</p>
                  ))}
                  <p className="mt-1 font-mono text-[11px] text-ink-3">Will not be applied</p>
                </>
              )}
              {result && <p className="mt-1 text-[12px] font-medium text-ink">{agilePlanResultLine(result)}</p>}
            </li>
          );
        })}
      </ul>
      {!settled && <p className="mt-2 font-mono text-[11px] text-ink-3">{expiry.text}</p>}
      {card.notice && <p className="mt-2 text-[12px] text-ink">{card.notice}</p>}
      {resultText && <p className="mt-2 whitespace-pre-wrap text-[12px] text-ink">{resultText}</p>}
      {!settled && (
        <div className="mt-3 flex gap-2">
          {!card.declineOnly && !expiry.expired && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => click('approve', onApprove)}
              className="rounded-md bg-coral px-2.5 py-1 text-[11px] font-medium text-white hover:bg-coral-hover disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
            >
              Approve
            </button>
          )}
          <button
            type="button"
            disabled={disabled}
            onClick={() => click('cancel', onDecline)}
            className="rounded-md border border-warm-border bg-paper px-2.5 py-1 text-[11px] font-medium text-ink-2 hover:border-warm-border-strong disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
